import React, { useMemo } from 'react';
import {
	useUserTermItemsQuery,
	useWorkerInformationQuery,
} from '../../../../../graphql/graphql';
// @ts-ignore
import styles from './TherapistDetail.module.scss';
import Loading from '../../../../../Components/Loading';
import { useAppContext } from '../../../../../Services/AppContext';
import TherapistDetailItem from './TherapistDetailItem';
import useWorkplaceRole from '../../../../Workplace/Hooks/useWorkplaceRole';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import TherapistInformation from '../../../../TherapistModule/Components/TherapistInformation/TherapistInformation';

interface Props {
	userId: string;
}

export interface MetaDataItem {
	title: string;
	key: string;
	value?: string;
	printValue?: string;
	defaultValues?: any;
}

const TherapistDetail: React.FC<Props> = ({ userId }) => {
	const { currency } = useAppContext();
	const { data, isLoading, refetch } = useWorkerInformationQuery({
		userId,
	});
	const termItems = useUserTermItemsQuery({
		id: userId,
	});

	const meta = useMemo(() => {
		const metas: Record<string, any> = {};
		data?.user?.meta?.forEach(({ key, value }: any) => {
			metas[key] = value;
		});
		return metas;
	}, [data]);

	const languages = useMemo(() => {
		const names: string[] = [];
		const values: SelectValue[] = [];
		termItems?.data?.user?.termItems
			?.filter(item => item?.term?.type === 'language_grouped')
			?.forEach(item => {
				if (item?.id && item?.name) {
					names.push(item.name);
					values.push({
						value: item.id,
						label: item.name,
					});
				}
			});
		return {
			defaultValues: { languages: values },
			names: names.join(', '),
		};
	}, [termItems?.data]);

	const insuranceCompanies = useMemo(() => {
		const names: string[] = [];
		const values: SelectValue[] = [];
		termItems?.data?.user?.termItems
			?.filter(item => item?.term?.type === 'insurance_companies')
			?.forEach(item => {
				if (item?.id && item?.name) {
					names.push(item.name);
					values.push({
						value: item.id,
						label: item.name,
					});
				}
			});
		return {
			defaultValues: { insuranceCompanies: values },
			names: names.join(', '),
		};
	}, [termItems?.data]);

	const isDataLoading = useMemo(() => {
		if (isLoading || termItems.isLoading) {
			return true;
		}
		return false;
	}, [isLoading, termItems.isLoading]);

	if (isDataLoading) {
		return <Loading />;
	}

	const { from, to, lengthOfConsultation } = meta['therapistPriceRange'] ?? {};
	const consultationTime = lengthOfConsultation
		? ` / ${lengthOfConsultation} min`
		: '';

	const userData: MetaDataItem[] = [
		{
			title: 'component.therapistDetail.therapistPriceRange',
			printValue: `${from ?? ''} ${currency} - ${
				to ?? ''
			} ${currency}${consultationTime}`,
			value: meta['therapistPriceRange'],
			key: 'therapistPriceRange',
		},
		{
			title: 'component.therapistDetail.insuranceCompanies',
			printValue: insuranceCompanies.names,
			value: insuranceCompanies.names,
			defaultValues: insuranceCompanies.defaultValues,
			key: 'insuranceCompanies',
		},
		{
			title: 'component.therapistDetail.language',
			printValue: languages.names,
			value: languages.names,
			defaultValues: languages.defaultValues,
			key: 'language',
		},
	];

	const handleReload = () => {
		refetch();
		termItems.refetch();
	};

	return (
		<div className={styles.wrapper}>
			{userData.map((item, index) => (
				<TherapistDetailItem
					item={item}
					key={index}
					onEditSuccess={handleReload}
					userId={userId}
				/>
			))}
			<TherapistInformation userId={userId} onReload={handleReload} />
		</div>
	);
};

export default TherapistDetail;
